import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Table as SemanticTable, Pagination, Dimmer, Icon } from 'semantic-ui-react';
import PageSizeSelect from './PageSizeSelect';
import TableHeader from './TableHeader';
import LoadingAnimation from '../Loaders/LoadingAnimation/LoadingAnimation';

import styles from './table.module.scss';

const getSortArrow = (dir, col, prop) => {
  if (dir && col === prop) {
    return dir === 'asc' ? <Icon name="chevron up" /> : <Icon name="chevron down" />;
  }
  return null;
};

const Table = (props) => {
  const {
    currentPage,
    direction,
    data,
    columns,
    limit,
    onChangeLimit,
    column,
    handleSort,
    totalPages,
    onPageChange,
    isLoading,
    showPageSizeSelector,
    handleRowClick,
    striped,
    rowSpacing,
  } = props;

  const { t } = useTranslation();

  if (!data) {
    return null;
  }

  const rows = data.map((row, index) => {
    const { id, rowId, isDisabled, draftId, isLastChecked } = row;
    const itemId = id || rowId || draftId || index;
    const rowKey = `${itemId}_${index}`;

    return (
      <React.Fragment key={rowKey}>
        <SemanticTable.Row
          className={cn({
            [styles.rowDisabled]: isDisabled,
            [styles.rowClickable]: !!handleRowClick,
            [styles.rowChecked]: isLastChecked,
          })}
          onClick={() => handleRowClick && handleRowClick(row)}
        >
          {columns.map((cell, cellIndex) => {
            const { property, title, customCell, sortable } = cell;

            let cellContent = '';

            if (customCell) {
              cellContent = customCell(row, cell, props, getSortArrow);
            } else {
              cellContent = (
                <>
                  <p
                    className={styles.expandTitle}
                    onClick={sortable && !isLoading ? () => handleSort(property) : () => {}}
                    role="presentation"
                  >
                    {t(title)}
                    {getSortArrow(direction, column, property)}
                  </p>
                  <p className={styles.expandProperty}>
                    {cell.format ? cell.format(row[property], row) : row[property]}
                  </p>
                </>
              );
            }

            const cellKey = property ? `${property}_${itemId}` : `customCell_${cellIndex}`;

            return <SemanticTable.Cell key={cellKey}>{cellContent}</SemanticTable.Cell>;
          })}
        </SemanticTable.Row>
        {row.additionalRow && row.additionalRow.isOpen && (
          <SemanticTable.Row className="additionalRow">
            <SemanticTable.Cell colSpan={columns.length}>
              <row.additionalRow.Component data={row.additionalRow.data} />
            </SemanticTable.Cell>
          </SemanticTable.Row>
        )}
        {!!rowSpacing && <tr style={{ height: `${rowSpacing}px` }} />}
      </React.Fragment>
    );
  });

  const noDataRow = (
    <SemanticTable.Row key="noDataRow" className={styles.noDataRow}>
      <SemanticTable.Cell colSpan={columns.length} key="noDataCell" textAlign="center">
        {t('paymentTbl.noData')}
      </SemanticTable.Cell>
    </SemanticTable.Row>
  );

  const showNoDataRow = !isLoading && !data.length;

  return (
    <>
      {totalPages > 1 && showPageSizeSelector && (
        <PageSizeSelect limit={+limit} onChangeLimit={onChangeLimit} />
      )}

      <SemanticTable striped={striped}>
        <TableHeader
          columns={columns}
          column={column}
          direction={direction}
          handleSort={handleSort}
          isLoading={isLoading}
          getSortArrow={getSortArrow}
        />
        <SemanticTable.Body>
          {isLoading && (
            <SemanticTable.Row
              key="tblLoaderRow"
              className={cn({
                [styles.loaderTr]: true,
                [styles.withPagination]: totalPages > 1,
              })}
            >
              <SemanticTable.Cell colSpan={columns.length} className={styles.loaderWrapper}>
                <Dimmer active inverted>
                  <div className={styles.loader}>
                    <LoadingAnimation />
                  </div>
                </Dimmer>
              </SemanticTable.Cell>
            </SemanticTable.Row>
          )}
          {showNoDataRow && noDataRow}
          {!!data.length && rows}
        </SemanticTable.Body>
      </SemanticTable>
      {totalPages > 1 && (
        <Pagination
          boundaryRange={0}
          siblingRange={1}
          className={styles.pagination}
          totalPages={totalPages}
          activePage={currentPage}
          onPageChange={onPageChange}
          firstItem={null}
          lastItem={null}
        />
      )}
    </>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  limit: PropTypes.number.isRequired,
  direction: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  column: PropTypes.string,
  handleSort: PropTypes.func.isRequired,
  showPageSizeSelector: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  handleRowClick: PropTypes.func,
  striped: PropTypes.bool,
  rowSpacing: PropTypes.number,
};

Table.defaultProps = {
  data: [],
  direction: PropTypes.oneOf(['asc', 'desc']),
  column: null,
  showPageSizeSelector: false,
  handleRowClick: null,
  striped: true,
  rowSpacing: 0,
};

export default Table;
